import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { DevToolSection, devFieldStack, devLabel, devMono, devResultPanel } from '/imports/ui/DevToolSection';
import { Input } from '/imports/ui/Input';
import { generateOtpFromMfaKey } from '/imports/api/devtools';

export const DevToolMfaOtp = () => {
  const [mfaKey, setMfaKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [remaining, setRemaining] = useState(0);

  // Tick the countdown so the operator can see when the current OTP expires.
  useEffect(() => {
    if (!otp || remaining <= 0) return;
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, [otp, remaining]);

  const run = async () => {
    if (!mfaKey.trim()) return toast.error('MFA key is required');
    setLoading(true);
    setOtp('');
    setRemaining(0);
    try {
      const res = await generateOtpFromMfaKey.call({ mfaKey });
      if (res.ok && res.otp) {
        setOtp(res.otp);
        setRemaining(res.expiresInSec ?? 30);
        try { await navigator.clipboard.writeText(res.otp); toast.success('OTP copied'); }
        catch { /* clipboard may be unavailable; the OTP is shown anyway */ }
      }
      else {
        toast.error(`OTP failed: ${res.error || 'unknown'}`);
      }
    }
    catch (e: any) {
      toast.error(e?.message || `${e}`);
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <DevToolSection
      title="Generate TOTP from MFA Key"
      description="Paste a TOTP secret (base32) and get the current 6-digit code. Useful for testing bot logins or one-off MTGO authentication."
      buttonText="Generate"
      onRun={run}
      loading={loading}
    >
      <div className={devFieldStack}>
        <div>
          <label className={devLabel}>MFA Key</label>
          <Input
            className="block w-full [&_input]:w-full"
            value={mfaKey}
            onChange={(v) => setMfaKey(v ?? '')}
            onEnter={() => run()}
            placeholder="JBSWY3DPEHPK3PXP"
            autoComplete="off"
          />
        </div>
      </div>
      {otp && (
        <div className={`${devResultPanel} flex items-center justify-between`}>
          <span className={`${devMono} text-[24px] font-semibold tracking-[0.15rem]`}>
            {otp}
          </span>
          <span className={remaining <= 5 ? 'text-sell' : 'text-fg-3'}>
            expires in {remaining}s
          </span>
        </div>
      )}
    </DevToolSection>
  );
};
